import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "@taxassist/api-client";
import {
  getRoleDisplayName,
  userCanAccessOps,
} from "../../utils/userPermissions";
import { OpsSidebar } from "./OpsSidebar";

/**
 * OpsLayoutRoute component that ensures user has Ops privileges
 * Renders Ops layout with sidebar for authorized users
 * Shows access denied for users without Ops privileges
 * Note: Authentication is handled by parent ProtectedRoute
 */
export const OpsLayoutRoute: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isAboveMobile = useMediaQuery(theme.breakpoints.up("sm"));

  // Check if user has ops privileges (Admin or Reviewer)
  // Authentication is already handled by ProtectedRoute
  if (!userCanAccessOps(user)) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Box textAlign="center">
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Ops privileges required to access this section.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your current role:{" "}
            {user?.role ? getRoleDisplayName(user.role as UserRole) : "Unknown"}
          </Typography>
        </Box>
      </Box>
    );
  }

  // User has ops privileges - render ops layout
  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 65px)" }}>
      {isAboveMobile && <OpsSidebar fixed />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          pb: 0,
          [theme.breakpoints.down("sm")]: {
            p: 0,
          },
          backgroundColor: (theme) => theme.palette.grey[100],
          overflowX: "hidden",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};
